import React, { useEffect, useMemo, useState } from "react";
import { L as Link } from "../lib/i18n";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Star, MessageSquare, Loader2, Edit3, Trash2, X, ShieldCheck, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "../lib/analytics";

const SORT_OPTIONS = [
  { value: "newest", label: "Neueste" },
  { value: "best", label: "Beste" },
  { value: "worst", label: "Schlechteste" },
];

const COMMENT_MAX = 1000;
const COMMENT_MIN = 3;

function StarRow({ value = 0, size = 16, interactive = false, onChange, testid }) {
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  return (
    <div className="inline-flex items-center gap-0.5" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((i) => {
        const filled = i <= shown;
        const half = !filled && i - 0.5 <= shown;
        return (
          <button
            key={i}
            type="button"
            disabled={!interactive}
            onMouseEnter={() => interactive && setHover(i)}
            onMouseLeave={() => interactive && setHover(0)}
            onClick={() => interactive && onChange?.(i)}
            className={`relative inline-flex transition ${interactive ? "cursor-pointer hover:scale-110" : "cursor-default"}`}
            data-testid={interactive ? `star-input-${i}` : undefined}
            aria-label={`${i} Stern${i > 1 ? "e" : ""}`}
          >
            <Star
              style={{ width: size, height: size }}
              className={filled ? "fill-amber-400 text-amber-400" : half ? "fill-amber-400/50 text-amber-400" : "fill-zinc-800 text-zinc-700"}
            />
          </button>
        );
      })}
    </div>
  );
}

function RatingSummary({ average, count }) {
  const rounded = Math.round((average || 0) * 10) / 10;
  return (
    <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-4" data-testid="reviews-summary">
      <div className="flex items-baseline gap-2">
        <span className="font-display text-4xl font-bold text-white tabular-nums sm:text-5xl" data-testid="reviews-average">
          {rounded.toFixed(1)}
        </span>
        <span className="text-sm text-zinc-500">/ 5.0</span>
      </div>
      <div className="flex flex-col gap-1">
        <StarRow value={average || 0} size={20} testid="reviews-summary-stars" />
        <span className="text-xs text-zinc-400" data-testid="reviews-count">
          {count === 1 ? "1 Bewertung" : `${count} Bewertungen`}
        </span>
      </div>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "gerade eben";
  if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min`;
  if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std`;
  if (diff < 30 * 86400) return `vor ${Math.floor(diff / 86400)} Tag${diff > 86400 * 2 ? "en" : ""}`;
  return d.toLocaleDateString("de-CH", { day: "2-digit", month: "short", year: "numeric" });
}

function ReviewItem({ review, isOwn, onEdit, onDelete, isAdmin, onAdminDelete }) {
  const initial = (review.reviewer_name || "?").trim().charAt(0).toUpperCase();
  return (
    <Card className="border-zinc-800 bg-zinc-950 p-5" data-testid={`review-item-${review.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-red-600 to-red-800 text-sm font-bold text-white">
            {initial}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-display text-sm font-semibold text-white" data-testid={`review-name-${review.id}`}>
                {review.reviewer_name || "Anonym"}
              </span>
              {review.verified_buyer && (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-300 ring-1 ring-emerald-500/30" title="Verifizierter Käufer">
                  <BadgeCheck className="h-3 w-3" /> Verifiziert
                </span>
              )}
            </div>
            <div className="mt-1 flex items-center gap-2">
              <StarRow value={review.rating} size={14} testid={`review-stars-${review.id}`} />
              <span className="text-xs text-zinc-500">
                {timeAgo(review.created_at)}
                {review.edited && " · bearbeitet"}
              </span>
            </div>
          </div>
        </div>
        {(isOwn || isAdmin) && (
          <div className="flex flex-shrink-0 gap-1">
            {isOwn && (
              <button
                onClick={onEdit}
                className="rounded-md p-1.5 text-zinc-400 transition hover:bg-zinc-900 hover:text-white"
                aria-label="Bearbeiten"
                data-testid={`review-edit-${review.id}`}
              >
                <Edit3 className="h-4 w-4" />
              </button>
            )}
            {isOwn && (
              <button
                onClick={onDelete}
                className="rounded-md p-1.5 text-zinc-400 transition hover:bg-red-500/10 hover:text-red-400"
                aria-label="Löschen"
                data-testid={`review-delete-${review.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
            {!isOwn && isAdmin && (
              <button
                onClick={onAdminDelete}
                className="rounded-md p-1.5 text-zinc-400 transition hover:bg-red-500/10 hover:text-red-400"
                aria-label="Admin: Löschen"
                data-testid={`review-admin-delete-${review.id}`}
                title="Admin: Bewertung entfernen"
              >
                <ShieldCheck className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>
      {review.comment && (
        <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-zinc-300" data-testid={`review-comment-${review.id}`}>
          {review.comment}
        </p>
      )}
    </Card>
  );
}

function ReviewForm({ slug, existing, onSaved, onCancel }) {
  const [rating, setRating] = useState(existing?.rating || 0);
  const [comment, setComment] = useState(existing?.comment || "");
  const [busy, setBusy] = useState(false);

  const isEdit = !!existing;
  const remaining = COMMENT_MAX - comment.length;

  const submit = async () => {
    if (!rating || rating < 1 || rating > 5) {
      toast.error("Bitte wähle 1 bis 5 Sterne");
      return;
    }
    if (comment && comment.trim().length > 0 && comment.trim().length < COMMENT_MIN) {
      toast.error(`Kommentar zu kurz (min ${COMMENT_MIN} Zeichen)`);
      return;
    }
    setBusy(true);
    try {
      const res = isEdit
        ? await api.updateDealerReview(slug, { rating, comment })
        : await api.createDealerReview(slug, { rating, comment });
      toast.success(isEdit ? "Bewertung aktualisiert" : "Danke für deine Bewertung!");
      trackEvent(isEdit ? "review_updated" : "review_created", {
        dealer_slug: slug,
        rating,
        has_comment: !!(comment && comment.trim().length > 0),
      });
      onSaved?.(res);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Fehler beim Speichern");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-red-500/30 bg-gradient-to-br from-red-950/20 via-zinc-950 to-zinc-950 p-5" data-testid="review-form">
      <div className="flex items-center justify-between">
        <h4 className="font-display text-sm font-bold uppercase tracking-wider text-red-300">
          {isEdit ? "Bewertung bearbeiten" : "Bewertung schreiben"}
        </h4>
        {onCancel && (
          <button onClick={onCancel} className="rounded-md p-1 text-zinc-500 hover:bg-zinc-900 hover:text-white" data-testid="review-form-cancel">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="mt-4 flex items-center gap-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Deine Wertung</span>
        <StarRow value={rating} interactive onChange={setRating} size={28} testid="review-form-stars" />
        {rating > 0 && <span className="text-sm font-semibold text-white">{rating}/5</span>}
      </div>

      <div className="mt-4">
        <Textarea
          rows={4}
          value={comment}
          onChange={(e) => setComment(e.target.value.slice(0, COMMENT_MAX))}
          placeholder="Wie war dein Erlebnis mit diesem Händler?"
          className="border-zinc-800 bg-black text-white placeholder:text-zinc-600"
          data-testid="review-form-comment"
        />
        <div className="mt-1 flex justify-end text-xs text-zinc-500">
          <span className={remaining < 50 ? "text-amber-400" : ""}>{remaining} Zeichen übrig</span>
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        {onCancel && (
          <Button variant="outline" onClick={onCancel} disabled={busy} className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-900">
            Abbrechen
          </Button>
        )}
        <Button
          onClick={submit}
          disabled={busy || !rating}
          className="bg-gradient-to-r from-red-600 to-orange-500 text-white hover:from-red-500 hover:to-orange-400"
          data-testid="review-form-submit"
        >
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {isEdit ? "Aktualisieren" : "Bewertung absenden"}
        </Button>
      </div>
    </Card>
  );
}

/**
 * Phase 60 — DealerReviews
 * Props:
 *  - slug: dealer slug
 *  - dealerId: dealer.id (to detect self-review attempts)
 *  - initialAverage / initialCount: hydrated from parent /api/dealers/{slug}
 *  - onAggregateChange(avg, count): bubble updated aggregates to the parent
 *    so JSON-LD AggregateRating re-renders without a full refetch.
 */
export default function DealerReviews({ slug, dealerId, initialAverage = 0, initialCount = 0, onAggregateChange }) {
  const { user } = useAuth();
  const [sort, setSort] = useState("newest");
  const [items, setItems] = useState([]);
  const [average, setAverage] = useState(initialAverage);
  const [count, setCount] = useState(initialCount);
  const [loading, setLoading] = useState(true);
  const [myReview, setMyReview] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const isOwnDealer = !!user && user.id === dealerId;
  const isAdmin = user?.role === "admin";
  const limit = 10;

  // Initial fetch + on sort change
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.listDealerReviews(slug, { sort, page: 1, limit })
      .then((res) => {
        if (!alive) return;
        setItems(res.items || []);
        setAverage(res.average || 0);
        setCount(res.count || 0);
        setHasMore((res.total || 0) > (res.items || []).length);
        setPage(1);
        onAggregateChange?.(res.average || 0, res.count || 0);
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [slug, sort]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load my review on auth change
  useEffect(() => {
    if (!user || isOwnDealer) { setMyReview(null); return; }
    let alive = true;
    api.getMyDealerReview(slug)
      .then((res) => alive && setMyReview(res?.review || null))
      .catch(() => alive && setMyReview(null));
    return () => { alive = false; };
  }, [slug, user, isOwnDealer]);

  const loadMore = async () => {
    const next = page + 1;
    try {
      const res = await api.listDealerReviews(slug, { sort, page: next, limit });
      setItems((curr) => [...curr, ...(res.items || [])]);
      setHasMore((res.total || 0) > items.length + (res.items || []).length);
      setPage(next);
    } catch (e) {
      toast.error("Fehler beim Laden");
    }
  };

  const onReviewSaved = (res) => {
    const newReview = res.review;
    setMyReview(newReview);
    setShowForm(false);
    // Refresh list head — replace own or prepend
    setItems((curr) => {
      const without = curr.filter((r) => r.id !== newReview.id);
      return [newReview, ...without];
    });
    if (res.aggregate) {
      setAverage(res.aggregate.average);
      setCount(res.aggregate.count);
      onAggregateChange?.(res.aggregate.average, res.aggregate.count);
    }
  };

  const deleteMine = async () => {
    if (!window.confirm("Bewertung wirklich löschen?")) return;
    try {
      const res = await api.deleteDealerReview(slug);
      toast.success("Bewertung gelöscht");
      setMyReview(null);
      setItems((curr) => curr.filter((r) => r.user_id !== user.id));
      if (res.aggregate) {
        setAverage(res.aggregate.average);
        setCount(res.aggregate.count);
        onAggregateChange?.(res.aggregate.average, res.aggregate.count);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const adminDelete = async (id) => {
    if (!window.confirm("Als Admin entfernen?")) return;
    try {
      const res = await api.adminDeleteDealerReview(id);
      toast.success("Bewertung entfernt");
      setItems((curr) => curr.filter((r) => r.id !== id));
      if (res.aggregate) {
        setAverage(res.aggregate.average);
        setCount(res.aggregate.count);
        onAggregateChange?.(res.aggregate.average, res.aggregate.count);
      }
    } catch (e) {
      toast.error("Fehler");
    }
  };

  const canWriteReview = !!user && !isOwnDealer && !myReview;
  const hasContent = items.length > 0;

  return (
    <section className="mt-10" data-testid="dealer-reviews-section">
      <header className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 font-display text-2xl font-bold text-white">
            <MessageSquare className="h-6 w-6 text-red-500" /> Bewertungen
          </h2>
          <p className="mt-1 text-xs text-zinc-500">Echte Erfahrungen von CarHive-Nutzern</p>
        </div>
        {count > 0 && <RatingSummary average={average} count={count} />}
      </header>

      {/* CTA / Form */}
      {showForm && (
        <div className="mb-6">
          <ReviewForm
            slug={slug}
            existing={myReview}
            onSaved={onReviewSaved}
            onCancel={() => setShowForm(false)}
          />
        </div>
      )}

      {!showForm && canWriteReview && (
        <Card className="mb-6 flex flex-wrap items-center justify-between gap-4 border-zinc-800 bg-zinc-950 p-5" data-testid="review-cta-write">
          <div>
            <p className="font-display text-sm font-semibold text-white">Hattest du Kontakt mit diesem Händler?</p>
            <p className="mt-1 text-xs text-zinc-400">Teile deine Erfahrung — hilft anderen Käufern</p>
          </div>
          <Button
            onClick={() => setShowForm(true)}
            className="bg-gradient-to-r from-red-600 to-orange-500 text-white hover:from-red-500 hover:to-orange-400"
            data-testid="review-open-form"
          >
            <Star className="mr-1.5 h-4 w-4" /> Bewertung schreiben
          </Button>
        </Card>
      )}

      {!showForm && myReview && (
        <Card className="mb-6 flex flex-wrap items-center justify-between gap-3 border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="review-mine-banner">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-400" />
            <p className="text-sm text-emerald-100">Du hast diesen Händler mit <strong>{myReview.rating}/5</strong> bewertet.</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowForm(true)} className="border-emerald-500/40 bg-transparent text-emerald-200 hover:bg-emerald-500/10" data-testid="review-mine-edit">
              <Edit3 className="mr-1.5 h-3.5 w-3.5" /> Bearbeiten
            </Button>
            <Button size="sm" variant="outline" onClick={deleteMine} className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-red-500/10 hover:text-red-300" data-testid="review-mine-delete">
              <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Löschen
            </Button>
          </div>
        </Card>
      )}

      {!user && (
        <Card className="mb-6 border-zinc-800 bg-zinc-950 p-5 text-sm text-zinc-400" data-testid="review-cta-login">
          <Link to="/login" className="font-semibold text-red-400 hover:text-red-300">Anmelden</Link> um eine Bewertung zu schreiben.
        </Card>
      )}

      {/* Sort */}
      {hasContent && (
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="reviews-sort">
          <span className="text-xs uppercase tracking-wider text-zinc-500">Sortieren:</span>
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setSort(opt.value)}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                sort === opt.value
                  ? "bg-red-600 text-white"
                  : "border border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-red-500/40 hover:text-red-300"
              }`}
              data-testid={`reviews-sort-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-zinc-500">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : !hasContent ? (
        <Card className="border-dashed border-zinc-800 bg-zinc-950 p-10 text-center" data-testid="reviews-empty">
          <MessageSquare className="mx-auto h-10 w-10 text-zinc-700" />
          <p className="mt-3 text-sm text-zinc-500">Noch keine Bewertungen. Sei der oder die Erste!</p>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="reviews-list">
          {items.map((r) => (
            <ReviewItem
              key={r.id}
              review={r}
              isOwn={user && r.user_id === user.id}
              isAdmin={isAdmin}
              onEdit={() => setShowForm(true)}
              onDelete={deleteMine}
              onAdminDelete={() => adminDelete(r.id)}
            />
          ))}
          {hasMore && (
            <div className="pt-2 text-center">
              <Button variant="outline" onClick={loadMore} className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-900" data-testid="reviews-load-more">
                Mehr Bewertungen laden
              </Button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// Named export for use in JSON-LD builders
export { StarRow, RatingSummary };
